import React, { useContext } from 'react';
import { connect } from 'react-redux';
import { AppContext } from '@prisjakt/ui-app-context';
import ArrowIconUp from 'mdi-react/ChevronUpIcon';
import ArrowIconDown from 'mdi-react/ChevronDownIcon';
import Fullscreen from 'mdi-react/FullscreenIcon';
import Button from '@prisjakt/ui-button';
import Tooltip from '@prisjakt/ui-tooltip-new';
import { AppState } from '../../../../reducers';
import SidebarHeaderDesktop from '../../components/sidebar-desktop/sidebar-header-desktop';
import FilterPanel from '../../../category/components/filter/components/panel';
import SelectedFilters from '../../../category/components/filter/components/selected-filters';
import {
  setHorizontalFilter,
  propertySearchToggleExpansion,
  expandSection,
  propertySearch,
} from '../../../category/actions/filter';
import sidebarActions from '../../actions/lists';

import * as S from './styles';

type Property = {
  item_type: string; // eslint-disable-line camelcase
  id: string;
  name: string;
  unit: string | null;
  description: string | null;
  children?: Property[];
  has_icon?: boolean; // eslint-disable-line camelcase
  type?: string;
};

type PropertyCategory = {
  item_type: string; // eslint-disable-line camelcase
  name: string;
  children: Property[];
};

type FilteredValues = {
  [key in number]: PropertyValue;
};

type PropertyValue = {
  max_matching_products?: number; // eslint-disable-line camelcase
  max_value?: number; // eslint-disable-line camelcase
  min_value?: number; // eslint-disable-line camelcase
  values: Value[];
};

type Value = {
  from?: number;
  to?: number;
  products: number;
  name?: string;
  id?: number;
};

type DispatchProps = {
  dispatchPropertySearchToggleExpansion: (name: string) => void;
  dispatchExpandSection: (name: string) => void;
  dispatchPropertySearch: (value: string) => void;
  dispatchSetHorizontalFilter: (show: boolean) => void;
  dispatchCloseLists: () => void;
};

type StateProps = {
  categoryId: string;
  filterId?: string;
  isBrandCategory: boolean;

  filteredCore: Property[];
  filteredSections: PropertyCategory[];
  filteredValues: FilteredValues;

  inputValue: string;
  disabledExpansions: string[];
  expandedCategories: string[];
};

type Props = DispatchProps & StateProps;

const SidebarFilter = ({
  categoryId,
  isBrandCategory,
  filteredCore,
  filteredValues,
  filteredSections,
  inputValue,
  disabledExpansions,
  expandedCategories,
  dispatchPropertySearchToggleExpansion,
  dispatchExpandSection,
  dispatchPropertySearch,
  dispatchSetHorizontalFilter,
  dispatchCloseLists,
}: Props) => {
  const { t } = useContext(AppContext);

  const isSearching = inputValue.length > 1;

  const showHorizontalFilter = () => {
    dispatchCloseLists();
    dispatchSetHorizontalFilter(true);
  };

  return (
    <div>
      <SidebarHeaderDesktop title={t('filter', 'filter.title')} />

      <div>
        <SelectedFilters categoryId={categoryId} />
        <S.InputWrapper>
          <S.Input
            placeholder={t('filter', 'properties.search-placeholder')}
            value={inputValue}
            onChange={event => {
              dispatchPropertySearch(event.target.value);
            }}
          />
        </S.InputWrapper>
        <div>
          {filteredCore.map(coreFilter => (
            <FilterPanel
              key={coreFilter.id}
              isBrandFilteredCategory={isBrandCategory}
              displayFilter={coreFilter}
              filteredValues={filteredValues}
            />
          ))}
        </div>
        {filteredSections.map((section: PropertyCategory) => {
          const showSection = isSearching
            ? disabledExpansions.indexOf(section.name) < 0 // Defaults to expanded
            : expandedCategories.indexOf(section.name) >= 0; // Defaults to collapsed

          // Remove empty sections
          if (
            section.children.filter(
              (subFilter: Property) =>
                (subFilter.item_type === 'category' && subFilter.children) ||
                (filteredValues &&
                  filteredValues[subFilter.id] &&
                  filteredValues[subFilter.id].max_matching_products)
            ).length === 0
          ) {
            return null;
          }

          return (
            <React.Fragment key={section.name}>
              <S.ButtonWrapper>
                <S.ExpansionButton
                  type="button"
                  onClick={() =>
                    isSearching
                      ? dispatchPropertySearchToggleExpansion(section.name)
                      : dispatchExpandSection(section.name)
                  }
                >
                  <div>{section.name}</div>
                </S.ExpansionButton>
                <S.ArrowButton
                  type="button"
                  onClick={() =>
                    isSearching
                      ? dispatchPropertySearchToggleExpansion(section.name)
                      : dispatchExpandSection(section.name)
                  }
                >
                  {showSection ? <ArrowIconUp /> : <ArrowIconDown />}
                </S.ArrowButton>
              </S.ButtonWrapper>
              {showSection && (
                <FilterPanel
                  key={section.name}
                  isBrandFilteredCategory={isBrandCategory}
                  displayFilter={section}
                  filteredValues={filteredValues}
                  hideName
                />
              )}
            </React.Fragment>
          );
        })}
        <S.ExpandFilterButton>
          <Tooltip
            placement="left"
            title={t('filter', 'button.toggle-filter-expand')}
          >
            <Button
              variant="icon"
              icon={<Fullscreen size={30} />}
              onClick={showHorizontalFilter}
              scaleIcon={false}
              size="large"
              ariaLabel={t('filter', 'button.toggle-filter-expand')}
            />
          </Tooltip>
        </S.ExpandFilterButton>
      </div>

      <div />
    </div>
  );
};

const mapStateToProps = (state: AppState): StateProps => ({
  categoryId: state.areas.category.filter.categoryId,
  filterId: state.areas.category.filter.filterId,
  isBrandCategory: state.areas.category.filter.isBrandCategory,
  expandedCategories: state.areas.category.filter.expandedCategories,
  filteredCore: state.areas.category.search.filteredCore,
  filteredValues: state.areas.category.search.filteredValues,
  filteredSections: state.areas.category.search.filteredSections,
  inputValue: state.areas.category.search.inputValue,
  disabledExpansions: state.areas.category.search.disabledExpansions,
});

const dispatchProps = {
  dispatchPropertySearchToggleExpansion: propertySearchToggleExpansion,
  dispatchExpandSection: expandSection,
  dispatchPropertySearch: propertySearch,
  dispatchSetHorizontalFilter: setHorizontalFilter,
  dispatchCloseLists: sidebarActions.closeLists,
};

export default connect(
  mapStateToProps,
  dispatchProps
)(SidebarFilter);
